import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { WS_URL } from '../config/api';

class WebSocketService {
  constructor() {
    this.client = null;
    this.connected = false;
    this.tabSessionBlocked = false;
    this.subscriptions = new Map(); // Map of channelId -> STOMP subscription
    this.callbacks = new Map(); // Map of channelId -> Set of callbacks
    this.topicCallbacks = new Map(); // topic -> callback, kept across reconnects
    this.pendingSubscriptions = new Map(); // Queue subscriptions that were attempted before connection
    this.pendingTopicSubscriptions = new Map(); // Queue topic subscriptions attempted before connection
    this.connectionReady = false; // Track if connection is fully ready for subscriptions
    this.lastInboundAt = 0;
    this.hadConnection = false;
    this.reconnectTimer = null;

    if (typeof document !== 'undefined') {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState !== "visible" || this.tabSessionBlocked) return;
        const token = localStorage.getItem('token');
        if (!token) return;
        if (!this.canSubscribe()) {
          console.log("👁️ Tab visible and socket down, reconnecting...");
          this.connect(token);
        }
      });
    }

    if (typeof window !== 'undefined') {
      window.setInterval(() => this.ensureHealthy(), 8000);
    }
  }

  createTransport() {
    // A new socket is required on every connect. Reusing one dead SockJS
    // instance is why refreshes were needed after a drop.
    //
    // Production /ws is SockJS-owned (raw Upgrade returns "Welcome to SockJS!").
    // Electron must use SockJS too. The desktop shell serves UI from
    // http://127.0.0.1 so /ws/info XHR works (file:// Origin null does not).
    console.log('🔌 SockJS transport:', WS_URL);
    return new SockJS(WS_URL, null, {
      transports: ['websocket', 'xhr-streaming', 'xhr-polling'],
    });
  }

  markInbound() {
    this.lastInboundAt = Date.now();
  }

  queueKnownSubscriptions() {
    this.callbacks.forEach((callbacks, channelId) => {
      const callback = callbacks.values().next().value;
      if (callback) {
        this.pendingSubscriptions.set(channelId, callback);
      }
    });
    this.topicCallbacks.forEach((callback, topic) => {
      if (callback) {
        this.pendingTopicSubscriptions.set(topic, callback);
      }
    });
  }

  dropLiveSubscriptions() {
    this.subscriptions.clear();
    this.connected = false;
    this.connectionReady = false;
  }

  ensureHealthy() {
    if (this.tabSessionBlocked) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    const token = this.token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
    if (!token) return;
    if (this.canSubscribe()) return;
    this.connect(token);
  }

  connect(token) {
    if (this.tabSessionBlocked) {
      console.log('WebSocket connect skipped: tab session inactive');
      return;
    }
    // Only skip if STOMP is actually connected (flags alone can be stale)
    if (this.canSubscribe()) {
      console.log('WebSocket already connected');
      return;
    }
    // Stale flags / half-dead client — clean up before reconnecting
    if (this.client) {
      try {
        this.client.deactivate();
      } catch (_) { /* ignore */ }
      this.client = null;
    }
    this.connected = false;
    this.connectionReady = false;
    this.token = token; // Store token for reconnection
    console.log('🔌 Connecting to WebSocket:', WS_URL);
    console.log('🔑 Using token:', token ? token.substring(0, 20) + '...' : 'NO TOKEN');
    
    this.queueKnownSubscriptions();
    this.client = new Client({
      webSocketFactory: () => this.createTransport(),
      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },
      debug: (str) => {
        // Only log errors, not all STOMP messages
        if (str.includes('ERROR') || str.includes('error')) {
          console.error('STOMP: ' + str);
        }
      },
      reconnectDelay: 2000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      onConnect: (frame) => {
        console.log('✅ Connected to WebSocket');
        console.log('Connection frame:', frame);
        this.connected = true;
        this.connectionReady = true;
        this.markInbound();
        const isReconnect = this.hadConnection;
        this.hadConnection = true;
        
        // Process any pending subscriptions that were queued before connection
        console.log(`🔄 Processing ${this.pendingSubscriptions.size} pending subscriptions...`);
        const pendingChannels = new Map(this.pendingSubscriptions);
        this.pendingSubscriptions.clear();
        pendingChannels.forEach((callback, channelId) => {
          console.log(`📡 Processing pending subscription for channel: ${channelId}`);
          this.subscribeToChannel(channelId, callback);
        });

        // Process any pending topic subscriptions
        const pendingTopics = new Map(this.pendingTopicSubscriptions);
        this.pendingTopicSubscriptions.clear();
        pendingTopics.forEach((callback, topic) => {
          if (topic === '/user/queue/reminders') {
            this.subscribeToReminders(callback);
          } else if (topic === '/user/queue/panel-access') {
            this.subscribeToPanelAccess(callback);
          } else if (topic === '/user/queue/tasks') {
            this.subscribeToTasks(callback);
          } else if (topic === '/user/queue/meetings') {
            this.subscribeToMeetings(callback);
          } else if (topic === '/user/queue/messages') {
            this.subscribeToMessageAcks(callback);
          } else {
            this.subscribeToTopic(topic, callback);
          }
        });
        
        this.onConnectCallback && this.onConnectCallback();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('workloop:ws-connected'));
          if (isReconnect) {
            window.dispatchEvent(new CustomEvent('workloop:ws-reconnected'));
          }
        }
      },
      onStompError: (frame) => {
        console.error('❌ STOMP error:', frame.headers['message']);
        console.error('Details:', frame.body);
        this.connected = false;
        this.connectionReady = false;
        this.onErrorCallback && this.onErrorCallback(frame);
      },
      onWebSocketClose: () => {
        console.log('⚠️ WebSocket connection closed');
        this.queueKnownSubscriptions();
        this.dropLiveSubscriptions();
        this.onDisconnectCallback && this.onDisconnectCallback();
      },
      onDisconnect: () => {
        console.log('⚠️ STOMP disconnected');
        this.queueKnownSubscriptions();
        this.dropLiveSubscriptions();
      },
    });

    this.client.activate();
  }

  disconnect() {
    if (this.client) {
      if (this.connected) {
        this.subscriptions.forEach((subscription) => {
          subscription.unsubscribe();
        });
      }
      this.subscriptions.clear();
      this.callbacks.clear();
      this.pendingSubscriptions.clear();
      this.pendingTopicSubscriptions.clear();
      this.client.deactivate();
    }
    this.token = null;
    this.connected = false;
    this.connectionReady = false;
  }

  setTabSessionBlocked(blocked) {
    this.tabSessionBlocked = Boolean(blocked);
    if (this.tabSessionBlocked) {
      this.disconnect();
    }
  }

  isTabSessionBlocked() {
    return this.tabSessionBlocked;
  }

  /** True only when STOMP can safely accept subscribe/publish. */
  canSubscribe() {
    return Boolean(
      this.connected
      && this.connectionReady
      && this.client
      && this.client.active
      && this.client.connected
    );
  }

  updateToken(newToken) {
    if (!newToken || newToken === this.token) {
      return;
    }

    const wasConnected = this.connected;
    this.token = newToken;

    if (!wasConnected || !this.client) {
      return;
    }

    console.log('🔄 Access token refreshed, reconnecting WebSocket...');

    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.subscriptions.clear();

    this.client.deactivate();
    this.client = null;
    this.connected = false;
    this.connectionReady = false;

    this.connect(newToken);
  }

  subscribeToChannel(channelId, callback) {
    // Add callback to the set of callbacks for this channel (always do this first)
    if (!this.callbacks.has(channelId)) {
      this.callbacks.set(channelId, new Set());
    }
    this.callbacks.get(channelId).add(callback);

    // If subscription already exists, just return it (don't create duplicate subscription)
    if (this.subscriptions.has(`channel_${channelId}`)) {
      console.log(`✅ Callback added to existing subscription for channel: ${channelId}`);
      return this.subscriptions.get(`channel_${channelId}`);
    }

    // If not connected yet, queue the subscription
    if (!this.canSubscribe()) {
      console.log(`⏳ WebSocket not ready, queueing subscription for channel: ${channelId}`);
      this.pendingSubscriptions.set(channelId, callback);
      return null;
    }

    const topic = `/topic/channel/${channelId}`;
    console.log(`📡 Subscribing to topic: ${topic} (connection ready: ${this.connectionReady})`);
    
    try {
      const subscription = this.client.subscribe(topic, (message) => {
        try {
          if (!message || !message.body) {
            console.error('❌ Empty message received');
            return;
          }
          
          const data = JSON.parse(message.body);
          this.markInbound();
          const timestamp = new Date().toISOString();
          console.log(`📨 [${timestamp}] Message received on ${topic}:`, {
            id: data.id,
            type: data.type,
            channelId: data.channelId,
            senderId: data.senderId,
            content: data.content?.substring(0, 50),
            receivedAt: timestamp
          });
          
          // Call all registered callbacks for this channel immediately
          const callbacks = this.callbacks.get(channelId);
          if (callbacks) {
            callbacks.forEach(cb => {
              try {
                const callbackStart = Date.now();
                cb(data);
                const callbackDuration = Date.now() - callbackStart;
                if (callbackDuration > 10) {
                  console.warn(`⚠️ Slow callback execution: ${callbackDuration}ms for channel ${channelId}`);
                }
              } catch (error) {
                console.error('❌ Error in callback:', error);
              }
            });
          } else {
            console.warn(`⚠️ No callbacks registered for channel ${channelId}`);
          }
        } catch (error) {
          console.error('❌ Error parsing WebSocket message:', error);
          console.error('Raw message body:', message.body);
        }
      });

      if (subscription) {
        this.subscriptions.set(`channel_${channelId}`, subscription);
        console.log(`✅ Successfully subscribed to channel: ${channelId} on topic: ${topic}`);
      } else {
        console.error('❌ Subscription returned null for channel:', channelId);
        // Queue it for retry
        this.pendingSubscriptions.set(channelId, callback);
      }
      
      return subscription;
    } catch (error) {
      console.error('❌ Error subscribing to channel:', error);
      console.error('Error details:', error.message, error.stack);
      // Queue it for retry
      this.pendingSubscriptions.set(channelId, callback);
      return null;
    }
  }

  subscribeToUserQueue(userId, callback) {
    if (!this.canSubscribe()) {
      console.warn('WebSocket not connected — cannot subscribe to user queue');
      return null;
    }

    try {
      const subscription = this.client.subscribe(`/user/${userId}/queue/messages`, (message) => {
        const data = JSON.parse(message.body);
        callback(data);
      });

      this.subscriptions.set(`user_${userId}`, subscription);
      return subscription;
    } catch (error) {
      console.error('❌ Error subscribing to user queue:', error);
      this.connected = false;
      this.connectionReady = false;
      return null;
    }
  }

  /**
   * Subscribe to reminder triggers for the current user.
   * Server sends to /user/queue/reminders (principal = username) and to /topic/reminders/{userId}.
   */
  subscribeToReminders(callback) {
    const dest = '/user/queue/reminders';
    if (callback) this.topicCallbacks.set(dest, callback);
    if (!this.canSubscribe()) {
      if (callback) {
        this.pendingTopicSubscriptions.set(dest, callback);
      }
      return null;
    }
    const key = `topic_${dest}`;
    if (this.subscriptions.has(key)) {
      return this.subscriptions.get(key);
    }
    console.log('📡 Subscribing to reminder user queue:', dest);
    try {
      const subscription = this.client.subscribe(dest, (message) => {
        try {
          const data = JSON.parse(message.body);
          console.log('🔔 [WebSocket] Reminder message received on /user/queue/reminders:', data?.type, data);
          callback(data);
        } catch (err) {
          console.error('Error parsing reminder message:', err);
        }
      });
      this.subscriptions.set(key, subscription);
      return subscription;
    } catch (error) {
      console.error('❌ Error subscribing to reminders:', error);
      this.connected = false;
      this.connectionReady = false;
      if (callback) {
        this.pendingTopicSubscriptions.set(dest, callback);
      }
      return null;
    }
  }

  unsubscribeFromReminders() {
    const key = 'topic_/user/queue/reminders';
    const sub = this.subscriptions.get(key);
    if (sub) {
      sub.unsubscribe();
      this.subscriptions.delete(key);
    }
    this.pendingTopicSubscriptions.delete('/user/queue/reminders');
    this.topicCallbacks.delete('/user/queue/reminders');
  }

  /**
   * Subscribe to panel-access events for the current user.
   * Server sends to /user/queue/panel-access and /topic/panel-access/{userId}.
   */
  subscribeToPanelAccess(callback) {
    const dest = '/user/queue/panel-access';
    if (callback) this.topicCallbacks.set(dest, callback);
    if (!this.canSubscribe()) {
      if (callback) {
        this.pendingTopicSubscriptions.set(dest, callback);
      }
      return null;
    }
    const key = `topic_${dest}`;
    if (this.subscriptions.has(key)) {
      return this.subscriptions.get(key);
    }
    console.log('📡 Subscribing to panel-access user queue:', dest);
    try {
      const subscription = this.client.subscribe(dest, (message) => {
        try {
          const data = JSON.parse(message.body);
          console.log('🔔 [WebSocket] Panel access event:', data?.type, data);
          callback(data);
        } catch (err) {
          console.error('Error parsing panel-access message:', err);
        }
      });
      this.subscriptions.set(key, subscription);
      return subscription;
    } catch (error) {
      console.error('❌ Error subscribing to panel-access:', error);
      this.connected = false;
      this.connectionReady = false;
      if (callback) {
        this.pendingTopicSubscriptions.set(dest, callback);
      }
      return null;
    }
  }

  unsubscribeFromPanelAccess() {
    const key = 'topic_/user/queue/panel-access';
    const sub = this.subscriptions.get(key);
    if (sub) {
      sub.unsubscribe();
      this.subscriptions.delete(key);
    }
    this.pendingTopicSubscriptions.delete('/user/queue/panel-access');
    this.topicCallbacks.delete('/user/queue/panel-access');
  }

  /**
   * Subscribe to task events for the current user.
   * Server sends to /user/queue/tasks and /topic/tasks/{userId}.
   */
  subscribeToTasks(callback) {
    const dest = '/user/queue/tasks';
    if (callback) this.topicCallbacks.set(dest, callback);
    if (!this.canSubscribe()) {
      if (callback) {
        this.pendingTopicSubscriptions.set(dest, callback);
      }
      return null;
    }
    const key = `topic_${dest}`;
    if (this.subscriptions.has(key)) {
      return this.subscriptions.get(key);
    }
      console.log('📡 Subscribing to tasks user queue:', dest);
    try {
      const subscription = this.client.subscribe(dest, (message) => {
        try {
          const data = JSON.parse(message.body);
          console.log('🔔 [WebSocket] Task event:', data?.type, data);
          const handler = this.topicCallbacks.get(dest) || callback;
          handler?.(data);
        } catch (err) {
          console.error('Error parsing task message:', err);
        }
      });
      this.subscriptions.set(key, subscription);
      return subscription;
    } catch (error) {
      console.error('❌ Error subscribing to tasks:', error);
      this.connected = false;
      this.connectionReady = false;
      if (callback) {
        this.pendingTopicSubscriptions.set(dest, callback);
      }
      return null;
    }
  }

  unsubscribeFromTasks() {
    const key = 'topic_/user/queue/tasks';
    const sub = this.subscriptions.get(key);
    if (sub) {
      sub.unsubscribe();
      this.subscriptions.delete(key);
    }
    this.pendingTopicSubscriptions.delete('/user/queue/tasks');
    this.topicCallbacks.delete('/user/queue/tasks');
  }

  /**
   * Subscribe to meeting events for the current user (scheduled / 15-min reminder).
   * Server sends to /user/queue/meetings and /topic/meetings/{userId}.
   */
  subscribeToMeetings(callback) {
    const dest = '/user/queue/meetings';
    if (callback) this.topicCallbacks.set(dest, callback);
    if (!this.canSubscribe()) {
      if (callback) {
        this.pendingTopicSubscriptions.set(dest, callback);
      }
      return null;
    }
    const key = `topic_${dest}`;
    if (this.subscriptions.has(key)) {
      return this.subscriptions.get(key);
    }
    console.log('📡 Subscribing to meetings user queue:', dest);
    try {
      const subscription = this.client.subscribe(dest, (message) => {
        try {
          const data = JSON.parse(message.body);
          console.log('🔔 [WebSocket] Meeting event:', data?.type, data);
          callback(data);
        } catch (err) {
          console.error('Error parsing meeting message:', err);
        }
      });
      this.subscriptions.set(key, subscription);
      return subscription;
    } catch (error) {
      console.error('❌ Error subscribing to meetings:', error);
      this.connected = false;
      this.connectionReady = false;
      if (callback) {
        this.pendingTopicSubscriptions.set(dest, callback);
      }
      return null;
    }
  }

  unsubscribeFromMeetings() {
    const key = 'topic_/user/queue/meetings';
    const sub = this.subscriptions.get(key);
    if (sub) {
      sub.unsubscribe();
      this.subscriptions.delete(key);
    }
    this.pendingTopicSubscriptions.delete('/user/queue/meetings');
    this.topicCallbacks.delete('/user/queue/meetings');
  }

  /**
   * Sender ACK queue. Server echoes the created MessageDto (including clientMessageId)
   * to /user/queue/messages on every create.
   */
  subscribeToMessageAcks(callback) {
    const dest = '/user/queue/messages';
    if (callback) this.topicCallbacks.set(dest, callback);
    if (!this.canSubscribe()) {
      if (callback) {
        this.pendingTopicSubscriptions.set(dest, callback);
      }
      return null;
    }
    const key = `topic_${dest}`;
    if (this.subscriptions.has(key)) {
      return this.subscriptions.get(key);
    }
    try {
      const subscription = this.client.subscribe(dest, (message) => {
        try {
          callback(JSON.parse(message.body));
        } catch (err) {
          console.error('Error parsing message ACK:', err);
        }
      });
      this.subscriptions.set(key, subscription);
      return subscription;
    } catch (error) {
      console.error('❌ Error subscribing to message ACKs:', error);
      this.connected = false;
      this.connectionReady = false;
      if (callback) {
        this.pendingTopicSubscriptions.set(dest, callback);
      }
      return null;
    }
  }

  unsubscribeFromMessageAcks() {
    const key = 'topic_/user/queue/messages';
    const sub = this.subscriptions.get(key);
    if (sub) {
      sub.unsubscribe();
      this.subscriptions.delete(key);
    }
    this.pendingTopicSubscriptions.delete('/user/queue/messages');
    this.topicCallbacks.delete('/user/queue/messages');
  }

  sendMessage(channelId, content, extra = {}) {
    if (!this.connected) {
      console.error('WebSocket not connected');
      return;
    }

    this.client.publish({
      destination: '/app/chat.sendMessage',
      body: JSON.stringify({
        channelId,
        content,
        ...extra,
      }),
    });
  }

  joinChannel(channelId, username) {
    if (!this.connected) {
      console.error('WebSocket not connected');
      return;
    }

    this.client.publish({
      destination: '/app/chat.addUser',
      body: JSON.stringify({
        channelId,
        username,
      }),
    });
  }

  sendTypingStatus(channelId, username, isTyping) {
    if (!this.connected) {
      console.error('❌ WebSocket not connected, cannot send typing status');
      return;
    }

    console.log('📤 Sending typing status via WebSocket:', {
      destination: '/app/chat.typing',
      channelId,
      username,
      isTyping
    });

    try {
      this.client.publish({
        destination: '/app/chat.typing',
        body: JSON.stringify({
          channelId,
          username,
          isTyping,
        }),
      });
      console.log('✅ Typing status sent successfully');
    } catch (error) {
      console.error('❌ Error sending typing status:', error);
    }
  }

  unsubscribeFromChannel(channelId, callback = null) {
    // If a specific callback is provided, only remove that callback
    if (callback) {
      const callbacks = this.callbacks.get(channelId);
      if (callbacks) {
        callbacks.delete(callback);
        console.log(`✅ Removed callback for channel: ${channelId}, remaining callbacks: ${callbacks.size}`);
        
        // If no more callbacks, unsubscribe from the topic
        if (callbacks.size === 0) {
          const subscription = this.subscriptions.get(`channel_${channelId}`);
          if (subscription) {
            subscription.unsubscribe();
            this.subscriptions.delete(`channel_${channelId}`);
            this.callbacks.delete(channelId);
            console.log(`✅ Unsubscribed from channel: ${channelId} (no more callbacks)`);
          }
        }
      }
    } else {
      // Remove all callbacks and unsubscribe
      const subscription = this.subscriptions.get(`channel_${channelId}`);
      if (subscription) {
        subscription.unsubscribe();
        this.subscriptions.delete(`channel_${channelId}`);
        this.callbacks.delete(channelId);
        console.log(`✅ Unsubscribed from channel: ${channelId}`);
      }
    }
  }

  unsubscribeFromUser(userId) {
    const subscription = this.subscriptions.get(`user_${userId}`);
    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(`user_${userId}`);
    }
  }

  subscribeToTopic(topic, callback) {
    if (callback) {
      if (!this.topicCallbackSets) this.topicCallbackSets = new Map();
      if (!this.topicCallbackSets.has(topic)) this.topicCallbackSets.set(topic, new Set());
      this.topicCallbackSets.get(topic).add(callback);
      this.topicCallbacks.set(topic, callback);
    }
    if (!this.canSubscribe()) {
      this.pendingTopicSubscriptions.set(topic, callback);
      return null;
    }

    if (this.subscriptions.has(`topic_${topic}`)) {
      return this.subscriptions.get(`topic_${topic}`);
    }

    try {
      const subscription = this.client.subscribe(topic, (message) => {
        try {
          this.markInbound();
          const data = JSON.parse(message.body);
          const handlers = this.topicCallbackSets?.get(topic);
          if (handlers && handlers.size) {
            handlers.forEach((handler) => {
              try {
                handler(data);
              } catch (handlerError) {
                console.error(`❌ Topic handler failed for ${topic}:`, handlerError);
              }
            });
            return;
          }
          const handler = this.topicCallbacks.get(topic) || callback;
          handler?.(data);
        } catch (error) {
          console.error(`❌ Error parsing message on topic ${topic}:`, error);
        }
      });
      this.subscriptions.set(`topic_${topic}`, subscription);
      return subscription;
    } catch (error) {
      console.warn(`⏳ STOMP not ready for ${topic}, queueing:`, error?.message || error);
      this.connected = false;
      this.connectionReady = false;
      this.pendingTopicSubscriptions.set(topic, callback);
      return null;
    }
  }

  unsubscribeFromTopic(topic) {
    const sub = this.subscriptions.get(`topic_${topic}`);
    if (sub) {
      try {
        sub.unsubscribe();
      } catch (error) {
        console.warn(`⚠️ Failed to unsubscribe from ${topic}:`, error?.message || error);
      }
      this.subscriptions.delete(`topic_${topic}`);
    }
    this.pendingTopicSubscriptions.delete(topic);
    this.topicCallbacks.delete(topic);
  }

  setOnConnect(callback) {
    this.onConnectCallback = callback;
  }

  setOnDisconnect(callback) {
    this.onDisconnectCallback = callback;
  }

  setOnError(callback) {
    this.onErrorCallback = callback;
  }

  isConnected() {
    return this.canSubscribe();
  }
  
  // Retry any failed subscriptions
  retryPendingSubscriptions() {
    if (!this.canSubscribe()) return;

    if (this.pendingSubscriptions.size > 0) {
      console.log(`🔄 Retrying ${this.pendingSubscriptions.size} pending subscriptions...`);
      const pending = new Map(this.pendingSubscriptions);
      this.pendingSubscriptions.clear();
      pending.forEach((callback, channelId) => {
        this.subscribeToChannel(channelId, callback);
      });
    }

    if (this.pendingTopicSubscriptions.size > 0) {
      console.log(`🔄 Retrying ${this.pendingTopicSubscriptions.size} pending topic subscriptions...`);
      const pendingTopics = new Map(this.pendingTopicSubscriptions);
      this.pendingTopicSubscriptions.clear();
      pendingTopics.forEach((callback, topic) => {
        if (topic === '/user/queue/reminders') {
          this.subscribeToReminders(callback);
        } else if (topic === '/user/queue/panel-access') {
          this.subscribeToPanelAccess(callback);
        } else if (topic === '/user/queue/tasks') {
          this.subscribeToTasks(callback);
        } else if (topic === '/user/queue/meetings') {
          this.subscribeToMeetings(callback);
        } else if (topic === '/user/queue/messages') {
          this.subscribeToMessageAcks(callback);
        } else {
          this.subscribeToTopic(topic, callback);
        }
      });
    }
  }
}

export default new WebSocketService();

